import { useEffect, useRef, useState } from 'react';
import {
  Configure,
  InstantSearchSSRProvider,
} from 'react-instantsearch-hooks-web';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { oneLine } from 'common-tags';

import Calendar from '@/icons/light/calendar.svg';
import ArrowLeft from '@/icons/light/chevron-left.svg';
import ArrowRight from '@/icons/light/chevron-right.svg';
import List from '@/icons/light/list.svg';
import MapPin from '@/icons/light/map-pin.svg';
import X from '@/icons/light/x.svg';
import { Container } from '@/atoms';

import {
  GridCardHits,
  HierarchicalMenu,
  InstantSearch,
  Pagination,
  Title,
} from '@/components/algolia';
import { useResizeObserver } from '@/hooks';
import { usePrevious } from '@/lib';
import { gtmPush } from '@/utils';

import type { TypeFlatAnalytics } from '@/types';
import type { Moment } from 'moment';
import type { FunctionComponent } from 'react';
import type { TypeListOfFlatsProps } from './types';

const ClusteringMapAlgolia = dynamic(
  () => import('@/components/algolia/clustering-map'),
  {
    ssr: true,
  }
);

const Modal = dynamic(() => import('@/atoms/modal'), {
  ssr: true,
});

const CalendarTabs = dynamic(
  () => import('@/components/molecules/calendar-tabs'),
  {
    ssr: true,
  }
);

const calculateHeight = (element: HTMLElement | undefined) => {
  if (!element) return 0;

  const height = element.offsetHeight;
  const marginTop = parseInt(
    window?.getComputedStyle(element)?.getPropertyValue('margin-top') ?? 0
  );
  // Este es el height del box shadow del Disclosure.Panel del HierarchicalMenu
  const BOX_SHADOW_HEIGHT = 12;

  return height + marginTop + BOX_SHADOW_HEIGHT;
};

const isMedEst = (type: TypeFlatAnalytics['ventaAlquiler']) =>
  type === 'MED_EST';

/**
 * ListOfFlats
 */
export const ListOfFlats: FunctionComponent<TypeListOfFlatsProps> = ({
  minBookingDays,
  serverState,
  type,
  defaultArea,
  typeText,
  initialUiState,
}: TypeListOfFlatsProps) => {
  const router = useRouter();
  const [mapFull, setMapFull] = useState(false);
  const [dates, setDates] = useState<[Moment, Moment] | []>([]);
  const [openDateFilter, setOpenDateFilter] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const prevAmount = usePrevious(mapFull);
  const entry = useResizeObserver(filterRef);
  const mapClasses = oneLine`
    ${
      mapFull
        ? 'w-full aspect-[3/4] md:aspect-video'
        : 'min-w-[550px] aspect-[3/4] hidden lg:flex'
    }
    h-full transition-all duration-300 ease-in-out
  `;

  useEffect(() => {
    if (prevAmount !== undefined) {
      gtmPush({
        event: 'map_interaction',
        map_action: mapFull ? 'open' : 'close',
      });
    }
  }, [mapFull]);

  return (
    <>
      {isMedEst(type) && minBookingDays && (
        <Modal
          isOpen={openDateFilter}
          closeModal={() => setOpenDateFilter(false)}
          unmount
          className="md:w-[710px]"
        >
          <CalendarTabs
            minDays={minBookingDays}
            onSubmit={async (values) => {
              await router.replace({
                query: {
                  ...router.query,
                  startDate: values.startDate.format('DD-MM-YYYY'),
                  endDate: values.endDate.format('DD-MM-YYYY'),
                },
              });
              setDates([values.startDate, values.endDate]);
              setOpenDateFilter(false);
            }}
            onReset={() => setDates([])}
            message={`Estancia mínima ${minBookingDays} días`}
          />
        </Modal>
      )}

      <InstantSearchSSRProvider {...(serverState ? serverState : {})}>
        <InstantSearch
          indexName={
            process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? ''
          }
          {...(initialUiState && { initialUiState })}
        >
          <Configure
            hitsPerPage={8}
            filters={`tipoDeUsoDescription:Vivienda AND ventaAlquiler:${type}`}
            {...(isMedEst(type) &&
              dates.length === 2 && {
                numericFilters: [
                  `fechaDisponibleDesde <= ${dates[0].unix()}`,
                  `fechaDisponibleHasta >= ${dates[1].unix()}`,
                ],
              })}
          />

          <Container
            wrapper
            padding=""
            className="scrollbar-hide z-10 overflow-x-auto pt-4"
            // Modo tricky para mostrar select en mobile con overflow-x-auto habría que cambiarlo a un modal los select
            style={{
              marginBottom: `-${calculateHeight(
                entry?.target as HTMLElement | undefined
              )}px`,
              paddingBottom: `${calculateHeight(
                entry?.target as HTMLElement | undefined
              )}px`,
            }}
            classNameWrapper="!flex-row lg:justify-between"
          >
            <div className="relative order-2 ml-1.5 flex flex-row gap-2 border-l border-neutrals-200 pl-1.5 pr-3 lg:order-1 lg:ml-0 lg:gap-4 lg:border-none lg:pl-0 lg:pr-0">
              {isMedEst(type) && (
                <button
                  onClick={async () => {
                    if (dates.length === 0) {
                      setOpenDateFilter(true);
                    } else {
                      await router.replace({
                        query: {},
                      });
                      setDates([]);
                    }
                  }}
                  className="inline-flex w-max items-center justify-center gap-1.5 rounded-full border border-neutrals-500 py-3 px-4 font-normal"
                >
                  <Calendar className="h-4 w-4" />

                  {dates.length === 0 ? (
                    'Disponibilidad'
                  ) : (
                    <>
                      Del {dates[0].format('DD/MM/YYYY')} al{' '}
                      {dates[1].format('DD/MM/YYYY')}
                      <X className="h-4 w-4" />
                    </>
                  )}
                </button>
              )}

              <HierarchicalMenu
                ref={filterRef}
                attributes={[
                  'hierarchicalZonas.lvl0',
                  'hierarchicalZonas.lvl1',
                  'hierarchicalZonas.lvl2',
                ]}
                classNames={{
                  button: 'md:max-w-max',
                  panel: 'md:max-w-max md:min-w-[330px]',
                }}
                sortBy={['isRefined']}
              />
            </div>

            <div className="relative order-1 ml-3 flex flex-row items-center justify-between rounded-full border border-neutrals-200 p-1 lg:order-2 lg:ml-0">
              <div
                className={oneLine`
                  duration-400 absolute -z-10 h-10 w-10 rounded-full bg-secondary-500 transition-transform ease-out
                  ${!mapFull ? 'translate-x-10' : ''}
                `}
              />

              <button
                onClick={() => setMapFull(!mapFull)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-text-neutrals-900"
              >
                <MapPin className="h-4 w-4" />
              </button>

              <button
                onClick={() => setMapFull(!mapFull)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-text-neutrals-900"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </Container>

          <Container
            wrapper
            classNameWrapper="lg:!flex-row gap-4 lg:gap-6"
            className="mt-4 border-t border-neutrals-100"
          >
            <div className={mapFull ? 'lg:hidden' : 'flex-1'}>
              <Title
                title={(currentArea) => `Pisos ${typeText} en ${currentArea}`}
                defaultArea={defaultArea}
                attributes={[
                  'hierarchicalZonas.lvl0',
                  'hierarchicalZonas.lvl1',
                  'hierarchicalZonas.lvl2',
                ]}
              />

              <GridCardHits className={mapFull ? 'hidden lg:block' : 'mt-4'} />

              <Pagination
                className={oneLine`
                  mt-6 flex-shrink-0 justify-end
                  ${mapFull ? 'hidden lg:block' : ''}
                `}
              />
            </div>

            <div
              className={`${mapClasses} sticky top-6 overflow-hidden rounded-lg`}
            >
              <button
                className={oneLine`
                  absolute top-6 left-6 z-10 flex items-center justify-center rounded-full bg-neutrals-0 text-primary-500 shadow-light-s
                  ${mapFull ? '' : 'h-10 w-10'}
                `}
                onClick={() => setMapFull(!mapFull)}
              >
                {mapFull ? (
                  <div className="flex flex-row gap-3 py-2.5 pl-4 pr-2 text-text-neutrals-900">
                    Ver listado{' '}
                    <ArrowRight className="h-6 w-6 text-primary-500" />
                  </div>
                ) : (
                  <ArrowLeft className="h-6 w-6" />
                )}
              </button>

              <ClusteringMapAlgolia className={mapClasses} />
            </div>
          </Container>
        </InstantSearch>
      </InstantSearchSSRProvider>
    </>
  );
};

ListOfFlats.displayName = 'ListOfFlats';
